import React, { memo } from 'react'

import moment from 'moment'

import { Fade, IconButton, ListItem, ListItemAvatar, ListItemText, Typography } from '@mui/material'
import { MoreVertOutlined } from '@mui/icons-material'

import { InviteStatus } from '@tabeeb/enums'
import UserAvatar from '@tabeeb/shared/userAvatar'

import UserRoleSelect from '@tabeeb/modules/../users/components/UserRoleSelect'

import { ContentUserRole, InviteWithQualificationsResponse } from '@tabeeb/contracts/main-api/swagger-main-api'
import { useContentUserQualifications } from '@tabeeb/modules/invites/hooks'
import { useSelector } from '@tabeeb/store'
import { getQualificationsForInvitesEnabled } from '@tabeeb/modules/appConfigState/selectors'
import useContentUserQualificationsRender from '@tabeeb/modules/contentUsers/hooks/useContentUserQualificationsRender'
import ExpirationStatusChip from './ExpirationStatusChip'
import LinkAccessChip from './LinkAccessChip'
import StatusBadge from './StatusBadge'

interface InviteProps {
  invite: InviteWithQualificationsResponse
  onOpenMenu: ({
    anchor,
    item,
  }: {
    anchor: EventTarget & HTMLButtonElement
    item: InviteWithQualificationsResponse
  }) => void
  onUpdateInviteRole: ({ Id, Role }: { Id: number; Role: ContentUserRole }) => void
}

const Invite: React.FC<InviteProps> = ({ invite, onUpdateInviteRole, onOpenMenu }) => {
  const recipient = invite.Recipient || { Name: invite.RecipientEmail, IsAccountDeleted: false }
  const qualificationForInvitesEnabled = useSelector(getQualificationsForInvitesEnabled)

  const status = invite.Accepted ? InviteStatus.Accepted : invite.Ignored ? InviteStatus.Declined : InviteStatus.Pending

  const {
    data: userQualifications,
    isLoading: areQualificationsLoading,
    isError: qualificationsFailedToLoad,
  } = useContentUserQualifications({
    userId: invite.RecipientIdentityGuid,
    contentId: invite.ContentId,
    qualificationIds: invite.RequiredQualificationIds || [],
    enabled: qualificationForInvitesEnabled,
  })

  const qualificationsComponent = useContentUserQualificationsRender({
    qualificationsFailedToLoad,
    userQualifications,
    areQualificationsLoading,
    qualificationForInvitesEnabled,
  })

  return (
    <Fade in>
      <ListItem disableGutters>
        <ListItemAvatar>
          <StatusBadge status={status}>
            <UserAvatar user={recipient} style={undefined} />
          </StatusBadge>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Typography
              sx={{ fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              noWrap
            >
              {recipient.Name}{' '}
              <Typography component='span' sx={{ fontSize: '10px', color: 'text.secondary', display: 'inline' }}>
                (Invited {moment(invite.CreatedOn).calendar()})
              </Typography>
            </Typography>
          }
          secondary={qualificationsComponent}
        />

        <ExpirationStatusChip invite={invite} />
        {invite.IdentityProtected && <LinkAccessChip />}
        {!invite.Accepted && (
          <UserRoleSelect
            role={invite.Role}
            onChange={({ target: { value: role } }) => onUpdateInviteRole({ Id: invite.Id, Role: role })}
          />
        )}
        <IconButton
          size='small'
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => onOpenMenu({ anchor: e.currentTarget, item: invite })}
        >
          <MoreVertOutlined />
        </IconButton>
      </ListItem>
    </Fade>
  )
}

export default memo<InviteProps>(Invite)
